import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Package, Plus, Users } from "lucide-react";

import useSEO from "../../lib/useSEO";
import {
  adminListLeads,
  adminListProducts,
} from "../../lib/api";
import { FORMAT_IDR } from "../../lib/constants";

export default function AdminDashboard() {
  const [products, setProducts] = useState([]);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);

  useSEO({ title: "Admin Dashboard — Ace Sublimasi Nusantara" });

  useEffect(() => {
    Promise.all([adminListProducts(), adminListLeads()])
      .then(([p, l]) => {
        setProducts(p);
        setLeads(l);
      })
      .finally(() => setLoading(false));
  }, []);

  const categories = products.reduce((acc, p) => {
    acc[p.category_name] = (acc[p.category_name] || 0) + 1;
    return acc;
  }, {});

  return (
    <div data-testid="admin-dashboard-page" className="space-y-8">
      <div>
        <p className="text-xs font-bold tracking-[0.3em] uppercase text-[#CCFF00] mb-2">
          / Dashboard
        </p>
        <h1 className="font-display text-4xl uppercase text-white leading-none">
          Selamat Datang di Command Center.
        </h1>
        <p className="mt-3 text-sm text-[#A3A3A3] max-w-2xl">
          Kelola produk, pantau leads, dan update katalog website dari sini.
          Perubahan akan langsung tampil di website.
        </p>
      </div>

      {/* Stat cards */}
      <div className="grid sm:grid-cols-3 gap-4">
        <StatCard
          testid="stat-products"
          label="Total Produk"
          value={loading ? "..." : products.length}
          icon={Package}
        />
        <StatCard
          testid="stat-leads"
          label="Leads Masuk"
          value={loading ? "..." : leads.length}
          icon={Users}
        />
        <StatCard
          testid="stat-categories"
          label="Kategori Aktif"
          value={loading ? "..." : Object.keys(categories).length}
          icon={Package}
        />
      </div>

      {/* Quick actions */}
      <div className="grid md:grid-cols-2 gap-4">
        <Link
          to="/admin/products/new"
          data-testid="dashboard-add-product"
          className="border border-[#262626] hover:border-[#CCFF00] p-6 flex items-start gap-4 transition-colors group"
        >
          <span className="w-12 h-12 bg-[#CCFF00] text-black inline-flex items-center justify-center shrink-0">
            <Plus className="w-5 h-5" />
          </span>
          <div>
            <p className="font-display text-xl uppercase text-white">
              Tambah Produk Baru
            </p>
            <p className="mt-1 text-sm text-[#A3A3A3]">
              Upload foto, isi harga, deskripsi, dan detail custom.
            </p>
            <span className="mt-3 inline-flex items-center gap-2 text-xs uppercase tracking-widest text-[#CCFF00]">
              Mulai <ArrowRight className="w-3.5 h-3.5" />
            </span>
          </div>
        </Link>

        <Link
          to="/admin/products"
          data-testid="dashboard-manage-products"
          className="border border-[#262626] hover:border-[#CCFF00] p-6 flex items-start gap-4 transition-colors group"
        >
          <span className="w-12 h-12 border border-[#262626] text-white inline-flex items-center justify-center shrink-0 group-hover:border-[#CCFF00] group-hover:text-[#CCFF00]">
            <Package className="w-5 h-5" />
          </span>
          <div>
            <p className="font-display text-xl uppercase text-white">
              Kelola Produk
            </p>
            <p className="mt-1 text-sm text-[#A3A3A3]">
              Edit harga, deskripsi, atau hapus produk yang sudah tidak relevan.
            </p>
            <span className="mt-3 inline-flex items-center gap-2 text-xs uppercase tracking-widest text-[#CCFF00]">
              Lihat Semua <ArrowRight className="w-3.5 h-3.5" />
            </span>
          </div>
        </Link>
      </div>

      {/* Recent leads */}
      <div className="border border-[#262626]">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#262626]">
          <p className="font-display uppercase text-white text-lg">
            Leads Terbaru
          </p>
          <span className="text-xs text-[#A3A3A3]">
            {loading ? "..." : `${leads.length} total`}
          </span>
        </div>
        {loading ? (
          <div className="p-6 text-sm text-[#A3A3A3]">Memuat...</div>
        ) : leads.length === 0 ? (
          <div className="p-6 text-sm text-[#A3A3A3]">
            Belum ada leads masuk.
          </div>
        ) : (
          <div className="divide-y divide-[#262626]">
            {leads.slice(0, 5).map((l) => (
              <div
                key={l.id}
                data-testid={`lead-row-${l.id}`}
                className="px-5 py-4 grid grid-cols-1 sm:grid-cols-[1fr_160px_120px] gap-2 sm:gap-4 text-sm"
              >
                <div>
                  <p className="text-white font-bold">{l.name}</p>
                  <p className="text-[#A3A3A3] text-xs truncate">
                    {l.message}
                  </p>
                </div>
                <div className="text-[#A3A3A3] text-xs">
                  <p>{l.email}</p>
                  <p>{l.phone}</p>
                </div>
                <div className="text-[#A3A3A3] text-xs sm:text-right">
                  {new Date(l.created_at).toLocaleString("id-ID", {
                    day: "2-digit",
                    month: "short",
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Top priced products */}
      <div className="border border-[#262626]">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#262626]">
          <p className="font-display uppercase text-white text-lg">
            Ringkasan Harga Produk
          </p>
          <Link
            to="/admin/products"
            data-testid="dashboard-view-products"
            className="text-xs uppercase tracking-widest text-[#CCFF00] hover:underline"
          >
            Kelola →
          </Link>
        </div>
        {loading ? (
          <div className="p-6 text-sm text-[#A3A3A3]">Memuat...</div>
        ) : (
          <div className="divide-y divide-[#262626]">
            {products.slice(0, 6).map((p) => (
              <div
                key={p.id}
                className="px-5 py-3 flex items-center justify-between gap-4 text-sm"
              >
                <div className="min-w-0">
                  <p className="text-white font-bold truncate">{p.name}</p>
                  <p className="text-xs text-[#A3A3A3] uppercase tracking-widest">
                    {p.category_name}
                  </p>
                </div>
                <span className="font-display text-lg text-[#CCFF00]">
                  {FORMAT_IDR(p.price_from)}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ testid, label, value, icon: Icon }) {
  return (
    <div
      data-testid={testid}
      className="border border-[#262626] p-5 flex items-start gap-4"
    >
      <span className="w-10 h-10 border border-[#262626] text-[#CCFF00] inline-flex items-center justify-center">
        <Icon className="w-4 h-4" />
      </span>
      <div>
        <p className="text-[10px] font-bold tracking-[0.25em] uppercase text-[#A3A3A3]">
          {label}
        </p>
        <p className="font-display text-3xl text-white mt-1">{value}</p>
      </div>
    </div>
  );
}
